/**
 * Monte Carlo simulation engine.
 * Runs N iterations of the estimation computation with randomized ratios,
 * producing probability distributions for key outputs.
 */

import { computeN1, FALLBACK_RATIOS } from './estimation';
import type { EstimationResult } from '../types/estimation';

export interface MonteCarloParam {
  key: string;
  label: string;
  base: number;
  min: number;
  max: number;
  distribution: 'uniform' | 'normal' | 'triangular';
}

export interface MonteCarloResult {
  iterations: number;
  outputs: {
    logements: number[];
    emplois: number[];
    cost_total: number[];
    sbp_total: number[];
    density: number[];
    classes: number[];
  };
  percentiles: {
    logements: Percentiles;
    emplois: Percentiles;
    cost_total: Percentiles;
    sbp_total: Percentiles;
    density: Percentiles;
    classes: Percentiles;
  };
  sensitivity: SensitivityItem[];
}

export interface Percentiles {
  p10: number;
  p25: number;
  p50: number;
  p75: number;
  p90: number;
  mean: number;
  std: number;
}

export interface SensitivityItem {
  key: string;
  label: string;
  correlation: number; // Spearman rank correlation with cost_total (-1 to +1)
}

/** Default simulation parameters with +/- 20% ranges */
export function getDefaultParams(tenantRatios: Record<string, number>): MonteCarloParam[] {
  const r = (key: string) => tenantRatios[key] ?? FALLBACK_RATIOS[key] ?? 0;
  return [
    {
      key: 'logements_per_ha',
      label: 'Logements / ha',
      base: r('logements_per_ha'),
      min: r('logements_per_ha') * 0.7,
      max: r('logements_per_ha') * 1.3,
      distribution: 'normal',
    },
    {
      key: 'inhabitants_per_logement',
      label: 'Habitants / logement',
      base: r('inhabitants_per_logement'),
      min: r('inhabitants_per_logement') * 0.85,
      max: r('inhabitants_per_logement') * 1.15,
      distribution: 'normal',
    },
    {
      key: 'emplois_per_logement_mixed',
      label: 'Emplois / logement',
      base: r('emplois_per_logement_mixed'),
      min: r('emplois_per_logement_mixed') * 0.6,
      max: r('emplois_per_logement_mixed') * 1.4,
      distribution: 'normal',
    },
    {
      key: 'infra_per_logement',
      label: 'Infrastructure / logement (CHF)',
      base: r('infra_per_logement'),
      min: r('infra_per_logement') * 0.75,
      max: r('infra_per_logement') * 1.35,
      distribution: 'triangular',
    },
    {
      key: 'sbp_per_logement',
      label: 'SBP / logement (m\u00B2)',
      base: r('sbp_per_logement'),
      min: r('sbp_per_logement') * 0.8,
      max: r('sbp_per_logement') * 1.2,
      distribution: 'normal',
    },
    {
      key: 'pct_activites_sbp',
      label: '% SBP Activit\u00E9s',
      base: r('pct_activites_sbp'),
      min: r('pct_activites_sbp') * 0.5,
      max: r('pct_activites_sbp') * 1.5,
      distribution: 'uniform',
    },
    {
      key: 'ecole_per_class',
      label: 'Co\u00FBt / classe (CHF)',
      base: r('ecole_per_class'),
      min: r('ecole_per_class') * 0.8,
      max: r('ecole_per_class') * 1.25,
      distribution: 'triangular',
    },
  ];
}

/** Random number generators by distribution type */
function sampleUniform(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

function sampleNormal(mean: number, min: number, max: number): number {
  // Box-Muller transform, clamped to [min, max]
  const std = (max - min) / 4; // ~95% within range
  let u1 = Math.random();
  const u2 = Math.random();
  if (u1 === 0) u1 = 0.0001;
  const z = Math.sqrt(-2.0 * Math.log(u1)) * Math.cos(2.0 * Math.PI * u2);
  return Math.max(min, Math.min(max, mean + z * std));
}

function sampleTriangular(min: number, max: number, mode: number): number {
  const u = Math.random();
  const fc = (mode - min) / (max - min);
  if (u < fc) {
    return min + Math.sqrt(u * (max - min) * (mode - min));
  }
  return max - Math.sqrt((1 - u) * (max - min) * (max - mode));
}

function sample(param: MonteCarloParam): number {
  switch (param.distribution) {
    case 'uniform':
      return sampleUniform(param.min, param.max);
    case 'normal':
      return sampleNormal(param.base, param.min, param.max);
    case 'triangular':
      return sampleTriangular(param.min, param.max, param.base);
    default:
      return param.base;
  }
}

function percentile(sorted: number[], p: number): number {
  const idx = (p / 100) * (sorted.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo]!;
  return sorted[lo]! + (sorted[hi]! - sorted[lo]!) * (idx - lo);
}

function calcPercentiles(values: number[]): Percentiles {
  const sorted = [...values].sort((a, b) => a - b);
  const n = sorted.length;
  const mean = sorted.reduce((a, b) => a + b, 0) / n;
  const variance = sorted.reduce((a, b) => a + (b - mean) ** 2, 0) / n;
  return {
    p10: percentile(sorted, 10),
    p25: percentile(sorted, 25),
    p50: percentile(sorted, 50),
    p75: percentile(sorted, 75),
    p90: percentile(sorted, 90),
    mean,
    std: Math.sqrt(variance),
  };
}

/** Spearman rank correlation */
function rankCorrelation(x: number[], y: number[]): number {
  const n = x.length;
  if (n < 3) return 0;

  const rank = (arr: number[]): number[] => {
    const indexed = arr.map((v, i) => ({ v, i }));
    indexed.sort((a, b) => a.v - b.v);
    const ranks = new Array<number>(n);
    for (let i = 0; i < n; i++) {
      ranks[indexed[i]!.i] = i + 1;
    }
    return ranks;
  };

  const rx = rank(x);
  const ry = rank(y);

  let sumD2 = 0;
  for (let i = 0; i < n; i++) {
    sumD2 += (rx[i]! - ry[i]!) ** 2;
  }

  return 1 - (6 * sumD2) / (n * (n * n - 1));
}

export function runMonteCarlo(
  ha: number,
  haZi: number,
  params: MonteCarloParam[],
  tenantRatios: Record<string, number>,
  tenantBenchmarks: Record<string, number>,
  iterations: number = 1000,
): MonteCarloResult {
  const outputs = {
    logements: [] as number[],
    emplois: [] as number[],
    cost_total: [] as number[],
    sbp_total: [] as number[],
    density: [] as number[],
    classes: [] as number[],
  };

  // Store sampled input values for sensitivity analysis
  const inputSamples: Record<string, number[]> = {};
  for (const p of params) {
    inputSamples[p.key] = [];
  }

  for (let i = 0; i < iterations; i++) {
    // Sample random ratios
    const iterRatios = { ...tenantRatios };
    for (const p of params) {
      const v = sample(p);
      iterRatios[p.key] = v;
      inputSamples[p.key]!.push(v);
    }

    // Run estimation
    const result: EstimationResult = computeN1(ha, haZi, iterRatios, tenantBenchmarks);

    outputs.logements.push(result.logements);
    outputs.emplois.push(result.emplois);
    outputs.cost_total.push(result.cost_total);
    outputs.sbp_total.push(result.sbp_total);
    outputs.density.push(result.density);
    outputs.classes.push(result.classes);
  }

  // Compute percentiles
  const percentiles = {
    logements: calcPercentiles(outputs.logements),
    emplois: calcPercentiles(outputs.emplois),
    cost_total: calcPercentiles(outputs.cost_total),
    sbp_total: calcPercentiles(outputs.sbp_total),
    density: calcPercentiles(outputs.density),
    classes: calcPercentiles(outputs.classes),
  };

  // Sensitivity: rank correlation of each input with cost_total
  const sensitivity: SensitivityItem[] = params.map((p) => ({
    key: p.key,
    label: p.label,
    correlation: rankCorrelation(inputSamples[p.key]!, outputs.cost_total),
  }));
  sensitivity.sort((a, b) => Math.abs(b.correlation) - Math.abs(a.correlation));

  return { iterations, outputs, percentiles, sensitivity };
}
